import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { AbstractService } from 'src/app/services/abstract.service';
import { RelatorioDinamicoService } from 'src/app/services/relatorio-dinamico.service';
import { MensagemIa } from './conversa-ia.component';
import { resumirProposta, rodapeUso } from './resumo-proposta';

@Component({
  selector: 'habil-criar-ia',
  templateUrl: './criar-ia.component.html',
  styleUrls: ['./criar-ia.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class CriarIaComponent implements OnInit {
  mensagens: MensagemIa[] = [];
  historico: { papel: string; texto: string }[] = [];
  proposta: any = null;
  resumo: { rotulo: string; valor: string }[] = [];
  pensando = false;
  salvando = false;

  resultado: any = null;
  rodandoPrevia = false;
  erroPrevia = '';

  podeEditar = false;
  podeSql = false;
  podeIncluir = false;

  altura: string = null;
  private elementoRaiz: HTMLDivElement;

  @ViewChild('raiz') set raiz(ref: ElementRef<HTMLDivElement>) {
    this.elementoRaiz = ref?.nativeElement;
    if (this.elementoRaiz) {
      setTimeout(() => this.ajustarAltura());
    }
  }

  @HostListener('window:resize')
  ajustarAltura(): void {
    if (!this.elementoRaiz || window.innerWidth <= 1100) {
      this.altura = null;
      return;
    }
    const topo = this.elementoRaiz.getBoundingClientRect().top + window.scrollY;
    this.altura = Math.max(560, window.innerHeight - topo - 16) + 'px';
  }

  constructor(
    public service: RelatorioDinamicoService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    breadcrumbService: BreadcrumbService,
    titleService: Title
  ) {
    titleService.setTitle('Criar com IA - ' + AbstractService.app);
    breadcrumbService.setItems([{ label: 'Relatórios dinâmicos', routerLink: '/relatorios-dinamicos' }, { label: 'Criar com IA' }]);
  }

  ngOnInit(): void {
    this.podeEditar = this.service.pode('E');
    this.podeSql = this.service.pode('S');
    this.podeIncluir = this.service.pode('I');
  }

  get fonteNova(): boolean {
    return !!this.proposta?.fonte && !this.proposta?.fonteExistente;
  }

  get podeSalvar(): boolean {
    return !!this.proposta?.modelo && !this.proposta?.erros?.length && this.podeIncluir && (!this.fonteNova || this.podeSql);
  }

  enviar(texto: string): void {
    this.mensagens = [...this.mensagens, { papel: 'usuario', texto }];
    this.historico.push({ papel: 'usuario', texto });
    this.pensando = true;
    const modo = this.proposta ? 'ajustar' : 'criar';
    this.service.proporIa(modo, this.historico, this.proposta?.modelo || null, this.fonteNova ? this.proposta.fonte : null).subscribe(
      (r) => {
        this.pensando = false;
        this.proposta = { ...r, fonteExistente: !r.fonte };
        this.resumo = resumirProposta(r.modelo, r.fonte);
        this.mensagens = [
          ...this.mensagens,
          { papel: 'assistente', texto: r.resposta || 'Proposta pronta.', avisos: r.avisos, erros: r.erros, rodape: rodapeUso(r) },
        ];
        this.historico.push({ papel: 'assistente', texto: r.resposta || 'Proposta pronta.' });
        if (!r.erros?.length) {
          this.rodarPrevia();
        }
      },
      (e) => {
        this.pensando = false;
        this.historico.pop();
        this.mensagens = [...this.mensagens, { papel: 'assistente', texto: this.service.mensagemErro(e), erros: [] }];
      }
    );
  }

  rodarPrevia(): void {
    if (!this.proposta?.modelo || !this.service.base) {
      return;
    }
    this.rodandoPrevia = true;
    this.erroPrevia = '';
    this.service.previaModelo(this.proposta.modelo, {}, this.fonteNova ? this.proposta.fonte : null).subscribe(
      (r) => {
        this.rodandoPrevia = false;
        this.resultado = r;
      },
      (e) => {
        this.rodandoPrevia = false;
        this.erroPrevia = this.service.mensagemErro(e);
      }
    );
  }

  descartar(): void {
    if (!this.proposta) {
      this.voltar();
      return;
    }
    this.confirmationService.confirm({
      message: 'Descartar a conversa e a proposta? Nada foi salvo.',
      accept: () => this.voltar(),
    });
  }

  revisarFonte(): void {
    this.service.importacaoPendente = {
      fonte: this.proposta.fonte,
      avisos: this.proposta.avisos || [],
      sugestaoModelo: this.proposta.modelo,
      variaveis: [],
      origem: 'Criar com IA',
    };
    this.router.navigate(['relatorios-dinamicos/fonte'], { queryParams: { id: 'nova' } });
  }

  salvar(): void {
    if (!this.podeSalvar) {
      return;
    }
    this.salvando = true;
    const criarModelo = () =>
      this.service.criarModelo(this.proposta.modelo, null).subscribe(
        (m) => {
          this.salvando = false;
          this.router.navigate(['relatorios-dinamicos/modelo'], { queryParams: { id: m.id } });
        },
        (e) => this.falha(e)
      );
    if (!this.fonteNova) {
      criarModelo();
      return;
    }
    this.service.criarFonte(this.proposta.fonte).subscribe(
      (f) =>
        this.service.salvarVersaoFonte(f.id, this.proposta.fonte, 'Criada com IA').subscribe(
          () => criarModelo(),
          (e) => this.falha(e)
        ),
      (e) => this.falha(e)
    );
  }

  voltar(): void {
    this.router.navigate(['relatorios-dinamicos']);
  }

  private falha(e: any): void {
    this.salvando = false;
    this.messageService.add({ severity: 'error', summary: 'Não salvou', detail: this.service.mensagemErro(e), life: 10000 });
  }
}
